import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { catchError, EMPTY, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import { AuthService, ContactUser } from '@aiwyn/auth';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { ignoreFalsy, Logger } from '@aiwyn/shared';
import { ClientApiService, JobApiService, StaffApiService } from '@aiwyn/api';
import { AppState } from '../../../state/app.state';
import { CxpClient, CxpClientRequestTaskModel, CxpServiceLine, CxpStaffMember, CxpTask } from '../models';
import { mapClientRequestToTask } from './utils/client-request-task-mappers.utils';
import { mapDeliverableToTask } from './utils/deliverable-task-mappers.utils';
import { mapStaffEntityToStaffMember } from './utils/staff-mappers.utils';
import { enrichTasks, mapTaskRowToCxpTask } from './utils/cxp-task-row-mappers.utils';
import { CxpDeliverableApiService, CxpDeliverableTaskApiResponse } from './cxp-deliverable-api.service';
import { CxpClientRequestApiService } from './cxp-client-request-api.service';
import { CxpCrlRestApiService } from './cxp-crl-rest-api.service';
import { CxpEsignApiService } from './cxp-esign-api.service';
import { expandEsignTasksBySigner } from './utils/esign-task-mappers.utils';

/**
 * Two interchangeable sources back the tasks list, selected by
 * `CXP_ENABLE_UNIFIED_TASKS_ENDPOINTS`:
 *
 *   ON  — one `GET /cxp/tasks/list` call; the backend aggregates CRLs +
 *         deliverables, scopes them to the user, and joins serviceLine /
 *         clientName per row.
 *   OFF — the Hasura fan-out: CRLs and deliverables fetched separately and
 *         mapped client-side, then a second round trip for serviceLine /
 *         clientName.
 *
 * Both branches emit the same `CxpTask[]`, so state, selectors and components
 * stay flag-unaware.
 *
 * This flag is independent of `CXP_ENABLE_REQUEST_LIST_REDESIGN`, which selects
 * the request detail surface. They describe different things — where a task row
 * comes from, versus which page opens when one is clicked — so all four
 * combinations are valid.
 */
@Injectable({ providedIn: 'root' })
export class CxpTasksService {
  private readonly authService = inject(AuthService);
  private readonly cxpClientRequestApi = inject(CxpClientRequestApiService);
  private readonly staffApi = inject(StaffApiService);
  private readonly jobApi = inject(JobApiService);
  private readonly clientApi = inject(ClientApiService);
  private readonly cxpDeliverableApi = inject(CxpDeliverableApiService);
  private readonly restApi = inject(CxpCrlRestApiService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly esignApi = inject(CxpEsignApiService);
  private readonly store = inject(Store);
  private readonly logger = inject(Logger);

  private readonly user = this.authService.getUserSignal(true);

  /**
   * A guest has no task list: they reach their work through the link, both
   * sources scope by contact identity, and the REST endpoints refuse a
   * secure-link caller. Guarded here so the surface stays unreachable however it
   * is entered — the CRL page and panel share the same background effects.
   */
  private get isGuest(): boolean {
    return this.user()?.isSecureLinkUser() === true;
  }

  fetchAll(): Observable<CxpTask[]> {
    if (this.isGuest) return of([]);
    return this.isUnifiedTasksEndpointsEnabled()
      .pipe(switchMap((enabled) => (enabled ? this.fetchAllFromRest() : this.fetchAllFromHasura())))
      .pipe(switchMap((tasks) => this.expandEsignSigners(tasks)));
  }

  /**
   * One eSign card per signature the caller owes, rather than per bundle. Neither
   * source carries recipients — both are built from the client-request shell — so the
   * distinction only exists in the signature-request read, and only that read can tell
   * two slots on one bundle apart.
   *
   * A failed read leaves the rows as they were rather than emptying the list: an
   * un-expanded eSign card still opens, and signing resolves a lone slot on its own.
   * Skipped entirely when there is no eSign row to expand, so the ordinary task list
   * costs no extra request.
   */
  private expandEsignSigners(tasks: CxpTask[]): Observable<CxpTask[]> {
    if (!tasks.some((task) => task.type === 'esign-client-request-list')) return of(tasks);

    return this.esignApi.listSignatureRequests().pipe(
      map((rows) => expandEsignTasksBySigner(tasks, rows)),
      catchError((err) => {
        this.logger.error('CxpTasksService :: failed to expand eSign tasks per signer', err);
        return of(tasks);
      }),
    );
  }

  /**
   * Open-count for the every-page nav badge. The REST path has a dedicated
   * cheap endpoint; the Hasura path has none, so it counts the full list as it
   * did before that endpoint existed.
   */
  fetchOpenTaskCount(): Observable<number> {
    if (this.isGuest) return of(0);
    return this.isUnifiedTasksEndpointsEnabled().pipe(
      switchMap((enabled) =>
        enabled
          ? this.restApi.getTaskCount().pipe(map((response) => response.open))
          : this.fetchAllFromHasura().pipe(map((tasks) => tasks.filter((task) => task.status === 'open').length)),
      ),
    );
  }

  /**
   * Resolves staff identities (name + email) for the staff sidebar. Neither
   * source joins staff detail — both carry only `staffIds`.
   */
  fetchStaff(staffIds: number[]): Observable<CxpStaffMember[]> {
    return this.staffApi.getStaff(staffIds).pipe(map((entities) => entities.map(mapStaffEntityToStaffMember)));
  }

  private isUnifiedTasksEndpointsEnabled(): Observable<boolean> {
    return this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.CXP_ENABLE_UNIFIED_TASKS_ENDPOINTS).pipe(take(1));
  }

  private fetchAllFromRest(): Observable<CxpTask[]> {
    return this.restApi.getTaskList().pipe(map((response) => response.items.map(mapTaskRowToCxpTask)));
  }

  private fetchAllFromHasura(): Observable<CxpTask[]> {
    return this.authService.getUser().pipe(
      ignoreFalsy(),
      take(1),
      switchMap((user) => {
        const contactUser = user as ContactUser;
        const defaultContactId = contactUser?.getContact()?.id;
        if (defaultContactId == null) return EMPTY;
        // Surface tasks assigned to ANY of the user's linked contacts, not just the
        // contact-record id. The full set comes from the `GetUserContactIds` Hasura
        // query, whose result is cached in AppState during user creation. Fall back to
        // the single contact id if the store hasn't been populated for some reason.
        const storedContactIds = this.store.selectSnapshot(AppState.getUserContactIds) ?? [];
        const contactIds = storedContactIds.length > 0 ? storedContactIds : [defaultContactId];
        const authEmails = collectAuthEmails(contactUser);
        return forkJoin({
          crls: this.fetchCrls(contactIds),
          deliverables: this.fetchDeliverables(contactIds),
        }).pipe(
          map(({ crls, deliverables }) => [
            ...crls.map((request) => mapClientRequestToTask(request, contactIds)).filter((task): task is CxpTask => task !== null),
            ...deliverables
              .flatMap((deliverable) => mapDeliverableToTask(deliverable, authEmails))
              .filter((task): task is CxpTask => task !== null),
          ]),
          switchMap((tasks) => this.joinSecondaryData(tasks)),
        );
      }),
    );
  }

  private fetchCrls(contactIds: number[]): Observable<CxpClientRequestTaskModel[]> {
    return this.cxpClientRequestApi.getClientRequestsForTasks(contactIds).pipe(
      catchError((err) => {
        this.logger.error('[CxpTasksService] getClientRequestsForTasks failed; degrading to empty:', err);
        return of<CxpClientRequestTaskModel[]>([]);
      }),
    );
  }

  private fetchDeliverables(contactIds: number[]): Observable<CxpDeliverableTaskApiResponse[]> {
    return this.cxpDeliverableApi.getDeliverablesForTasks(contactIds).pipe(
      catchError((err) => {
        this.logger.error('[CxpTasksService] getDeliverablesForTasks failed; degrading to empty:', err);
        return of<CxpDeliverableTaskApiResponse[]>([]);
      }),
    );
  }

  /**
   * Fills the `serviceLine` / `clientName` that REST rows already arrive with.
   * A failure costs those two display fields only, so rows still render rather
   * than the whole list failing.
   */
  private joinSecondaryData(tasks: CxpTask[]): Observable<CxpTask[]> {
    const jobIds = [...new Set(tasks.map((task) => task.jobId).filter((id): id is number => id !== null))];
    const clientIds = [...new Set(tasks.map((task) => task.clientId))];
    if (jobIds.length === 0 && clientIds.length === 0) return of(tasks);

    return this.fetchSecondaryData(jobIds, clientIds).pipe(
      map(({ serviceLines, clients }) => enrichTasks(tasks, serviceLines, clients)),
      catchError((error) => {
        this.logger.warn('[CxpTasksService] secondary data failed; rows render without service line / client name:', error);
        return of(tasks);
      }),
    );
  }

  private fetchSecondaryData(jobIds: number[], clientIds: number[]): Observable<SecondaryData> {
    return forkJoin({
      serviceLines:
        jobIds.length > 0
          ? this.jobApi.getBaseServiceLinesByJobIds(jobIds).pipe(
              map((lines) => {
                const grouped = new Map<number, CxpServiceLine>();
                for (const sl of lines) {
                  const existing = grouped.get(sl.id);
                  if (existing) {
                    existing.jobIds.push(sl.jobId);
                  } else {
                    grouped.set(sl.id, { id: sl.id, name: sl.name, jobIds: [sl.jobId] });
                  }
                }
                return [...grouped.values()];
              }),
            )
          : of<CxpServiceLine[]>([]),
      clients:
        clientIds.length > 0
          ? this.clientApi.getBaseClients(clientIds).pipe(map((clients) => clients.map((c): CxpClient => ({ id: c.id, name: c.name }))))
          : of<CxpClient[]>([]),
    });
  }
}

interface SecondaryData {
  serviceLines: CxpServiceLine[];
  clients: CxpClient[];
}

/**
 * Collects every email we can attribute to the logged-in user — both the
 * contact-record email (what the firm sent SafeSend) and the auth provider
 * email (what the user signed in with). The contact-record `email` field can
 * legacy-hold multiple comma-joined addresses, so we expand on `,` before
 * deduping. Lowercased so the mapper can do a case-insensitive Set lookup
 * against signer emails.
 */
function collectAuthEmails(user: ContactUser): string[] {
  const sources = [user?.getContact()?.contactInfo?.email, user?.getProviderEmail()];
  const collected = new Set<string>();
  for (const source of sources) {
    if (!source) continue;
    for (const part of source.split(',')) {
      const trimmed = part.trim().toLowerCase();
      if (trimmed.length > 0) collected.add(trimmed);
    }
  }
  return [...collected];
}
